// Shared constants for the Meal Planner feature set.

export const RATING_TAGS = [
  { id: 'family_loved', label: 'Family Loved' },
  { id: 'make_again', label: 'Make Again' },
  { id: 'too_spicy', label: 'Too Spicy' },
  { id: 'too_bland', label: 'Too Bland' },
  { id: 'kids_liked', label: 'Kids Liked' },
  { id: 'skip_next_time', label: 'Skip Next Time' },
  { id: 'healthy', label: 'Healthy' },
  { id: 'heavy', label: 'Too Heavy' },
];

export const PREF_LEVELS = [
  { val: 'love', label: 'Love', color: 'text-red-400 bg-red-500/10' },
  { val: 'like', label: 'Like', color: 'text-green-400 bg-green-500/10' },
  { val: 'neutral', label: 'OK', color: 'text-neutral-400 bg-white/5' },
  { val: 'dislike', label: 'Dislike', color: 'text-yellow-400 bg-yellow-500/10' },
  { val: 'never', label: 'Never', color: 'text-red-500 bg-red-500/15' },
];

export const DAY_MOODS = ['', 'light', 'regular', 'special_heavy', 'festive', 'fasting'];

export const MEAL_SLOTS = [
  { key: 'breakfast', label: 'Breakfast' },
  { key: 'lunch', label: 'Lunch' },
  { key: 'dinner', label: 'Dinner' },
  { key: 'snacks', label: 'Tea / Evening snack' },
];

// Phase 55 — Advanced Options for AI Chef
export const SLOT_PILLS = [
  { key: 'breakfast', label: 'Breakfast', emoji: '🍳' },
  { key: 'lunch', label: 'Lunch', emoji: '🍛' },
  { key: 'dinner', label: 'Dinner', emoji: '🌙' },
  { key: 'snacks', label: 'Tea / Snack', emoji: '🫖' },
];
export const STYLE_PILLS = [
  { key: 'simple', label: 'Simple', emoji: '🍃', desc: 'main + bread + rice' },
  { key: 'standard', label: 'Standard', emoji: '🍛', desc: 'main + bread + rice + raita' },
  { key: 'elaborate', label: 'Elaborate', emoji: '🎉', desc: '+ soup + salad + starter + dessert' },
];
export const STYLE_DEFAULT_EXTRAS = {
  simple: [],
  standard: ['raita'],
  elaborate: ['soup', 'salad', 'starter', 'dessert'],
};
export const EXTRA_PILLS = [
  { key: 'soup', label: 'Soup', emoji: '🥣' },
  { key: 'salad', label: 'Salad', emoji: '🥗' },
  { key: 'starter', label: 'Starter', emoji: '🥟' },
  { key: 'raita', label: 'Raita / Curd', emoji: '🥛' },
  { key: 'dessert', label: 'Dessert', emoji: '🍮' },
];
export const OCCASION_PILLS = [
  { key: 'regular', label: 'Regular' },
  { key: 'festive', label: 'Festive 🪔' },
  { key: 'fasting', label: 'Fasting / Vrat' },
  { key: 'kids_exam', label: 'Kids exam week' },
];
export const CUISINE_PILLS = ['South Indian', 'Punjabi', 'Gujarati', 'Bengali', 'Maharashtrian', 'Rajasthani', 'Chinese', 'Continental', 'Mediterranean'];

// Phase 83 — AI Chef Smart Input helpers
// Static one-tap chips for the "Avoid this week" field
export const AVOID_QUICK_CHIPS = [
  { label: 'No repeats', hint: 'no repeated dishes from last 2 weeks' },
  { label: 'No heavy foods', hint: 'no heavy or oily foods' },
  { label: 'No spicy', hint: 'no spicy dishes, mild seasoning only' },
  { label: 'No fried', hint: 'no fried food' },
  { label: 'No oily', hint: 'less oil, healthy cooking' },
  { label: 'No paneer', hint: 'no paneer this week' },
  { label: 'No dal daily', hint: 'skip dal on at least 3 days' },
  { label: 'No leftovers', hint: 'no repeated dishes across meals' },
];

// Static one-tap chips for the "Extra Instructions" field
export const INSTRUCTION_QUICK_CHIPS = [
  { label: 'Kids-friendly', hint: 'kids-friendly dishes - mild spice, familiar textures' },
  { label: 'Festival mode', hint: 'festive week - traditional sweets + rich dishes' },
  { label: 'Diet mode', hint: 'diet mode - low oil, high protein, portion controlled' },
  { label: 'Light dinner', hint: 'keep dinners light and easy to digest' },
  { label: 'Quick to cook', hint: 'each meal should be quick to cook (under 30 min)' },
  { label: 'Guests coming', hint: 'guests visiting - plan one elaborate meal with dessert' },
  { label: 'Budget week', hint: 'budget-friendly ingredients only, avoid exotic items' },
  { label: 'Batch cook', hint: 'plan batch-cooking friendly meals for the week' },
  { label: 'High protein', hint: 'each meal must include a good protein source' },
  { label: 'Fasting / Vrat', hint: 'vrat food only - no onion garlic, use sabudana/kuttu/singhara' },
];

// System-level instruction templates (savable / applicable in one tap)
export const INSTRUCTION_TEMPLATES = [
  { label: 'School week', text: 'kids-friendly dishes, quick to cook, light dinners, packed lunches must travel well' },
  { label: 'Festive week', text: 'festive week - traditional sweets + rich dishes, one elaborate lunch, plan for guests on weekend' },
  { label: 'Diet week', text: 'diet mode - low oil, high protein, portion controlled, no fried, no dessert except fruit' },
  { label: 'Fasting week', text: 'vrat food only - no onion garlic, use sabudana/kuttu/singhara, one meal a day, fruits allowed' },
  { label: 'Guests visiting', text: 'guests staying full week - plan variety, one elaborate lunch and one dinner party menu, avoid repeats' },
  { label: 'Budget week', text: 'budget-friendly ingredients only, avoid exotic items, use seasonal veggies, batch cook where possible' },
];

// Phase 47 — Occasion Menus
export const COURSE_OPTIONS = [
  { key: 'starters', label: 'Starters', icon: '🥗' },
  { key: 'mains', label: 'Mains', icon: '🍛' },
  { key: 'sides', label: 'Sides / Sabzi', icon: '🥦' },
  { key: 'breads', label: 'Breads', icon: '🫓' },
  { key: 'rice', label: 'Rice & Pulao', icon: '🍚' },
  { key: 'desserts', label: 'Desserts', icon: '🍮' },
  { key: 'drinks', label: 'Drinks', icon: '🥤' },
];

// Merchant deep-link search URLs for the smart shopping list.
export const MERCHANTS = [
  { id: 'bigbasket', label: 'BigBasket', short: 'BB', color: '#84C341', textColor: '#FFFFFF',
    url: (q) => `https://www.bigbasket.com/ps/?q=${encodeURIComponent(q)}` },
  { id: 'zepto', label: 'Zepto', short: 'Z', color: '#7B2CBF', textColor: '#FFFFFF',
    url: (q) => `https://www.zeptonow.com/search?query=${encodeURIComponent(q)}` },
  { id: 'blinkit', label: 'Blinkit', short: 'BL', color: '#F8CB46', textColor: '#1F1B16',
    url: (q) => `https://blinkit.com/s/?q=${encodeURIComponent(q)}` },
  { id: 'jiomart', label: 'JioMart', short: 'JM', color: '#0066B3', textColor: '#FFFFFF',
    url: (q) => `https://www.jiomart.com/search/${encodeURIComponent(q)}` },
];
